import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  FlexRow,
  formatDateShort,
  formatDateWithMonthAndYear,
  PageTile,
  useMatchMedia,
  Checkbox,
  CheckboxFontSize,
} from '@make-software/cspr-ui';
import { BodyText } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import {
  GraphComponent,
  GraphComponentDataFormatType,
  YAxisRangeStartType,
} from './graph-component';

enum TimeFrameTypes {
  'week' = '1 w',
  'month' = '1 mo',
  'quarter' = '3 mo',
  'year' = '1 yr',
  'all' = 'All time',
}

type TimeFrameType = {
  label: string;
  days: number;
  formatter: (value: any) => string;
};

export const timeFrameValues: TimeFrameType[] = [
  { label: TimeFrameTypes.week, days: 7, formatter: formatDateShort },
  { label: TimeFrameTypes.month, days: 30, formatter: formatDateShort },
  { label: TimeFrameTypes.quarter, days: 90, formatter: formatDateShort },
  {
    label: TimeFrameTypes.year,
    days: 365,
    formatter: formatDateWithMonthAndYear,
  },
  {
    label: TimeFrameTypes.all,
    days: 9999,
    formatter: formatDateWithMonthAndYear,
  },
];

export const customColorsByIndex = {
  0: 'contentGreen',
  1: 'contentLightBlue',
  2: 'contentYellow',
};

const ChartBlock = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    width: ['100%', '100%', '100%'],
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: ['center', 'center', 'flex-start'],
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    padding: ['20px 16px 32px', '20px 20px 36px'],
  })
);

const TitleContainer = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: ['column', 'column', 'row'],
    alignItems: ['flex-start', 'flex-start', 'center'],
    justifyContent: 'space-between',
    width: '100%',
    '& > * + *': {
      marginTop: [32, 32, 0],
    },
  })
);

export const CustomButtonContainer = styled.div<{ active: boolean }>(
  ({ theme, active }) =>
    theme.withMedia({
      border: 'none',
      cursor: 'pointer',
      color: theme.styleguideColors.contentPrimary,
      background: theme.styleguideColors.fillSecondary,
      borderRadius: theme.borderRadius.base,
      fontWeight: theme.typography.fontWeight.regular,
      minHeight: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: ['4px 12px'],
      ':hover': {
        background: theme.styleguideColors.fillSecondaryBlueHover,
      },
      ':active': {
        background: theme.styleguideColors.fillSecondaryBlueHover,
        fontWeight: theme.typography.fontWeight.semiBold,
      },
      ...(active && {
        background: theme.styleguideColors.fillSecondaryBlueHover,
        fontWeight: theme.typography.fontWeight.semiBold,
      }),
    })
);

const CheckboxListContainer = styled.div(({ theme }) =>
  theme.withMedia({
    marginLeft: [0, 120, 60, 63],
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    '& > * + *': {
      marginLeft: [24, 32, 32],
    },
    '& >:last-child': {
      marginTop: [28, 0, 0],
      marginLeft: [0, 32, 32],
    },
  })
);

const CustomButton = ({
  value,
  onClick,
  active,
}: {
  value: string;
  onClick: () => void;
  active: boolean;
}) => {
  return (
    <CustomButtonContainer onClick={onClick} active={active}>
      <BodyText size={active ? 1 : 3} variation="black">
        {value}
      </BodyText>
    </CustomButtonContainer>
  );
};

export const TimeFrameButtons = ({ handleChange, activeTimeFrame }) => {
  const { t } = useTranslation();
  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);

  return (
    <FlexRow justify={'end'} align={'center'} itemsSpacing={12}>
      {screenType === 'tabletOrAbove' && (
        <BodyText variation="lightGray" size={3}>
          <Trans t={t}>Time frame</Trans>
        </BodyText>
      )}
      <FlexRow itemsSpacing={4}>
        {timeFrameValues.map((item) => (
          <CustomButton
            onClick={() => handleChange(item)}
            value={item.label}
            active={item.label === activeTimeFrame.label}
            key={item.label}
          />
        ))}
      </FlexRow>
    </FlexRow>
  );
};

export interface TimeValuesChartProps {
  data: GraphComponentDataFormatType[];
  loading: boolean;
  title: string;
  description?: string;
  handleChartPeriodChange: (value: number) => void;
  yAxisTickFormatter: (value: any) => string;
  tooltipFormatter?: (value: any) => string;
  yAxisLabelText?: string;
  axisLine?: boolean;
  yAxisValueKey: string;
  yAxisRangeStart?: YAxisRangeStartType;
}

export const TimeValuesChart = (props: TimeValuesChartProps) => {
  const { data, handleChartPeriodChange, title } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const [selectedCheckboxKeys, setSelectedCheckboxKeys] = useState<string[]>(
    []
  );

  const selectedDataKeys = data
    .filter((item) => (item.checkboxLabel ? item.initiallySelected : true))
    .map((item) => item.graphKey);

  useEffect(() => {
    setSelectedCheckboxKeys(selectedDataKeys);
  }, []);

  const [activeTimeFrame, setActiveTimeFrame] = useState<TimeFrameType>(
    timeFrameValues[1]
  );

  const handleCheckboxChange = (key) => {
    if (selectedCheckboxKeys.length === 1 && selectedCheckboxKeys.includes(key))
      return;

    if (selectedCheckboxKeys.includes(key)) {
      setSelectedCheckboxKeys(
        selectedCheckboxKeys.filter((selectedKey) => selectedKey !== key)
      );
    } else {
      setSelectedCheckboxKeys([...selectedCheckboxKeys, key]);
    }
  };

  const handleTimeFrameChange = (timeframe: TimeFrameType) => {
    handleChartPeriodChange(timeframe.days);
    setActiveTimeFrame(timeframe);
  };

  const dataWithMatchingColors = data.map((item, idx) => ({
    ...item,
    themeColor: theme.styleguideColors[customColorsByIndex[idx]],
  }));

  const filteredData = dataWithMatchingColors.filter((item) =>
    selectedCheckboxKeys.includes(item.graphKey)
  );

  const multipleGraphChart = dataWithMatchingColors.length > 1;

  return (
    <PageTile>
      <ChartBlock>
        <TitleContainer>
          <BodyText scale={'xl'} size={1} variation="black" noWrap>
            <Trans t={t}>{title}</Trans>
          </BodyText>
          <TimeFrameButtons
            handleChange={handleTimeFrameChange}
            activeTimeFrame={activeTimeFrame}
          />
        </TitleContainer>

        <GraphComponent
          graphData={filteredData}
          {...props}
          multipleGraphChart={multipleGraphChart}
          xAxisTickFormatter={activeTimeFrame.formatter}
        />

        {multipleGraphChart && (
          <CheckboxListContainer>
            {dataWithMatchingColors.map((item, idx) => (
              <Checkbox
                key={idx}
                label={item.checkboxLabel}
                checked={selectedCheckboxKeys.includes(item.graphKey)}
                customCheckedColor={item.themeColor}
                customUncheckedColor={theme.styleguideColors.contentTertiary}
                onChange={() => handleCheckboxChange(item.graphKey)}
                checkboxFontSize={CheckboxFontSize.small}
              />
            ))}
          </CheckboxListContainer>
        )}
      </ChartBlock>
    </PageTile>
  );
};
